/**
 * ADORACARE-PATIENT-MASTER-B2 — the merge queue admin view
 * (`/dashboard/patients/merge-queue`, coc+ / "branch_manager and
 * above"). Lists pending fuzzy-match candidates from
 * patient_merge_queue with a side-by-side compare, and submits
 * approve / reject decisions through the
 * lib/domain/patient/merge state machine server-side.
 *
 * Approving REQUIRES an explicit survivor choice — there is no default
 * winner, because a wrong default is exactly how silent bad merges
 * happen. Every decision writes an audit_events row server-side
 * (founder rule); decided rows are terminal (re-raising is a new
 * pending row). matched_on carries SIGNALS only (never raw values), so
 * the compare card shows the B0 disambiguation fields per candidate.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import {
  fetchPatientMasterMergeQueue,
  postPatientMasterMergeDecision,
} from '@/dashboard/api';
import {
  genderAgeLabel,
  patientRecordHref,
  type MergeQueueItem,
  type MergeQueueResponse,
  type PatientCandidateCard,
} from '@/dashboard/patients/patient-master-view';

export interface MergeDecisionDraft {
  readonly queueId: number;
  readonly decision: 'approve' | 'reject';
  readonly survivorPatientId: string | null;
}

export interface PatientMergeQueueContentProps {
  readonly loading: boolean;
  readonly error: string | null;
  readonly data: MergeQueueResponse | null;
  /** queueId → chosen survivor (approve needs an explicit pick). */
  readonly survivorChoices: Readonly<Record<number, string>>;
  readonly onSurvivorChange?: (queueId: number, patientId: string) => void;
  readonly onDecide?: (draft: MergeDecisionDraft) => void;
  /** queueId currently submitting (disables that card's buttons). */
  readonly decidingQueueId: number | null;
  /** Last decision feedback line, e.g. "Pair #12 merged." */
  readonly notice: string | null;
}

function CandidateCell({
  candidate,
  picked,
  pickInputName,
  onPick,
}: {
  candidate: PatientCandidateCard;
  picked: boolean;
  pickInputName: string;
  onPick?: (() => void) | undefined;
}): JSX.Element {
  return (
    <div className="flex-1 rounded-md border border-border px-3 py-2">
      <label className="flex items-start gap-2 text-sm">
        <input
          type="radio"
          name={pickInputName}
          checked={picked}
          onChange={() => onPick?.()}
          aria-label={`Keep ${candidate.fullName} (${candidate.umr})`}
        />
        <span>
          <span className="font-medium text-foreground">
            {candidate.fullName}
          </span>
          <span className="mt-0.5 block text-xs text-muted-foreground">
            {genderAgeLabel(candidate.gender, candidate.ageYears)}
            {candidate.city ? ` · ${candidate.city}` : ''}
          </span>
          <span className="mt-0.5 block text-xs text-muted-foreground">
            {candidate.registeredClinicLabel ??
              (candidate.registeredClinicId !== null
                ? `Clinic #${candidate.registeredClinicId}`
                : 'No registered clinic')}
            {' · '}registered {candidate.createdAt.slice(0, 10)}
          </span>
          <span className="mt-0.5 block font-mono text-xs">
            {candidate.umr}
          </span>
          <Link
            to={patientRecordHref(candidate.patientId)}
            className="mt-1 inline-block text-xs font-medium text-primary underline-offset-4 hover:underline"
          >
            Open record
          </Link>
        </span>
      </label>
    </div>
  );
}

function matchedOnSummary(matchedOn: Record<string, unknown>): string {
  const parts: string[] = [];
  if (matchedOn.phone === true) parts.push('same phone');
  if (typeof matchedOn.name_similarity === 'number') {
    parts.push(`name similarity ${(matchedOn.name_similarity * 100).toFixed(0)}%`);
  }
  if (matchedOn.dob === true) parts.push('same date of birth');
  return parts.length > 0 ? parts.join(' · ') : 'signals unavailable';
}

function MergeQueueCard({
  item,
  survivorChoice,
  onSurvivorChange,
  onDecide,
  deciding,
}: {
  item: MergeQueueItem;
  survivorChoice: string | undefined;
  onSurvivorChange?: ((queueId: number, patientId: string) => void) | undefined;
  onDecide?: ((draft: MergeDecisionDraft) => void) | undefined;
  deciding: boolean;
}): JSX.Element {
  const pickName = `survivor-${item.queueId}`;
  return (
    <li className="rounded-md border border-border px-4 py-3">
      <p className="text-sm text-muted-foreground">
        Pair #{item.queueId} · match score{' '}
        {(item.matchScore * 100).toFixed(0)}% · {matchedOnSummary(item.matchedOn)}
      </p>
      <div className="mt-2 flex flex-col gap-3 sm:flex-row">
        <CandidateCell
          candidate={item.candidateA}
          picked={survivorChoice === item.candidateA.patientId}
          pickInputName={pickName}
          onPick={() => onSurvivorChange?.(item.queueId, item.candidateA.patientId)}
        />
        <CandidateCell
          candidate={item.candidateB}
          picked={survivorChoice === item.candidateB.patientId}
          pickInputName={pickName}
          onPick={() => onSurvivorChange?.(item.queueId, item.candidateB.patientId)}
        />
      </div>
      <p className="mt-2 text-xs text-muted-foreground">
        Select the record to KEEP, then approve — the other record is
        marked merged into it (reversible, audited). Reject keeps both
        patients as they are.
      </p>
      <div className="mt-2 flex gap-2">
        <button
          type="button"
          disabled={deciding || survivorChoice === undefined}
          onClick={() =>
            onDecide?.({
              queueId: item.queueId,
              decision: 'approve',
              survivorPatientId: survivorChoice ?? null,
            })
          }
          className="rounded-md border border-border bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground disabled:opacity-50"
        >
          {deciding ? 'Working…' : 'Approve merge'}
        </button>
        <button
          type="button"
          disabled={deciding}
          onClick={() =>
            onDecide?.({
              queueId: item.queueId,
              decision: 'reject',
              survivorPatientId: null,
            })
          }
          className="rounded-md border border-border px-3 py-1.5 text-sm font-medium disabled:opacity-50"
        >
          Reject
        </button>
      </div>
    </li>
  );
}

/**
 * Pure presentational body (no fetch) — rendered directly in tests via
 * react-dom/server (wrapped in a Router for links).
 */
export function PatientMergeQueueContent({
  loading,
  error,
  data,
  survivorChoices,
  onSurvivorChange,
  onDecide,
  decidingQueueId,
  notice,
}: PatientMergeQueueContentProps): JSX.Element {
  return (
    <section aria-labelledby="merge-queue-heading">
      <h1 id="merge-queue-heading">Patient merge queue</h1>
      <p className="text-sm text-muted-foreground">
        Possible duplicate patients found by the matching ladder. Merges
        are human decisions: nothing is merged automatically, every
        decision is audited, and merges stay reversible.
      </p>

      {notice ? (
        <p className="mt-2 text-sm text-foreground" role="status">
          {notice}
        </p>
      ) : null}

      {loading ? (
        <p className="loading mt-3" role="status" aria-live="polite">
          Loading merge queue…
        </p>
      ) : error ? (
        <p className="mt-3 text-sm text-destructive" role="alert">
          {error}
        </p>
      ) : data === null || data.items.length === 0 ? (
        <p className="mt-3 text-sm text-muted-foreground" role="status">
          No pending merge candidates. New fuzzy matches appear here when
          a registration overlaps an existing patient.
        </p>
      ) : (
        <ul className="mt-3 max-w-3xl space-y-3">
          {data.items.map((item) => (
            <MergeQueueCard
              key={item.queueId}
              item={item}
              survivorChoice={survivorChoices[item.queueId]}
              onSurvivorChange={onSurvivorChange}
              onDecide={onDecide}
              deciding={decidingQueueId === item.queueId}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

export default function PatientMergeQueuePage(): JSX.Element {
  const [data, setData] = useState<MergeQueueResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [survivorChoices, setSurvivorChoices] = useState<
    Record<number, string>
  >({});
  const [decidingQueueId, setDecidingQueueId] = useState<number | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = (): void => {
    setLoading(true);
    setError(null);
    fetchPatientMasterMergeQueue()
      .then((res) => {
        setData(res);
        setLoading(false);
      })
      .catch((err: unknown) => {
        setError(
          err instanceof Error ? err.message : 'Failed to load merge queue',
        );
        setLoading(false);
      });
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onDecide = (draft: MergeDecisionDraft): void => {
    if (decidingQueueId !== null) return;
    if (draft.decision === 'approve' && draft.survivorPatientId === null) {
      return;
    }
    setDecidingQueueId(draft.queueId);
    setNotice(null);
    postPatientMasterMergeDecision(draft.queueId, {
      decision: draft.decision,
      ...(draft.survivorPatientId !== null
        ? { survivorPatientId: draft.survivorPatientId }
        : {}),
    })
      .then((res) => {
        setDecidingQueueId(null);
        setNotice(
          res.status === 'merged'
            ? `Pair #${res.queueId} merged.`
            : `Pair #${res.queueId} rejected — both records kept.`,
        );
        load();
      })
      .catch((err: unknown) => {
        setDecidingQueueId(null);
        setError(err instanceof Error ? err.message : 'Decision failed');
      });
  };

  return (
    <PatientMergeQueueContent
      loading={loading}
      error={error}
      data={data}
      survivorChoices={survivorChoices}
      onSurvivorChange={(queueId, patientId) =>
        setSurvivorChoices((prev) => ({ ...prev, [queueId]: patientId }))
      }
      onDecide={onDecide}
      decidingQueueId={decidingQueueId}
      notice={notice}
    />
  );
}
